import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import type { Employee } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import { getFullName } from '@/shared/utils/employee-utils';
import type { EditableFact } from '../../shared/types';
import type { MatchPointSession } from '../../deep-dive-stage/types/deepDive.types';
import { ParticipantAvatar } from './ParticipantAvatar';
import { LessonRow } from './LessonRow';
import { FactCard } from './FactCard';
import { PendingLessonRow } from './PendingLessonRow';
import { collectAnswers, collectLessons } from '../utils/turn-helpers';

interface LeaderColumnProps {
  leader: Employee;
  session: MatchPointSession;
  fact: EditableFact | null;
  onEditLesson: (lessonIndex: number, lesson: string) => void;
  onDeleteLesson: (lessonIndex: number) => void;
  onAddLesson: (lesson: string) => void;
}

export function LeaderColumn({
  leader,
  session,
  fact,
  onEditLesson,
  onDeleteLesson,
  onAddLesson,
}: LeaderColumnProps) {
  const { t } = useTranslation('debriefV2');
  const fullName = getFullName(leader) ?? '';

  const answers = collectAnswers(session.turns);
  const lessons = collectLessons(session.turns);
  const questions = t('lessons.questions', {
    returnObjects: true,
  }) as readonly string[];

  const [pendingLesson, setPendingLesson] = useState<string | null>(null);
  useEffect(() => {
    setPendingLesson(null);
  }, [session]);

  const handlePendingCommit = (next: string) => {
    const trimmed = next.trim();
    if (trimmed) {
      onAddLesson(trimmed);
    }
    setPendingLesson(null);
  };

  return (
    <div className="w-[380px] shrink-0 flex flex-col bg-white rounded-2xl ring-1 ring-slate-900/5 shadow-md overflow-hidden">
      <div className="p-5 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <ParticipantAvatar employee={leader} size="lg" />
          <div className="min-w-0">
            <h4 className="font-semibold text-slate-900 text-sm truncate">
              {fullName}
            </h4>
          </div>
        </div>
        <span className="ms-2 px-2 py-1 bg-slate-900 text-white rounded text-[10px] font-bold uppercase tracking-widest shrink-0">
          {t('lessons.leaderPill')}
        </span>
      </div>

      <div className="p-5 flex flex-col">
        <div className="space-y-2.5 mb-4 shrink-0">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {t('lessons.factInFocusLabel')}
          </label>
          <FactCard
            fact={fact}
            manualFactLabel={t('lessons.manualFactLabel')}
          />
        </div>

        <div className="space-y-4 shrink-0">
          {questions.map((question, index) => (
            <div key={index} className="space-y-2.5">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {question}
              </label>
              <div className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 leading-relaxed shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)] whitespace-pre-wrap break-words min-h-[44px]">
                {answers[index] ?? ''}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 border-t border-slate-100 pt-5 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-900">
              {t('lessons.lessonsHeading')}
            </label>
            <span
              className={cn(
                'text-[11px] font-medium text-slate-400',
                lessons.length > 0 && 'bg-slate-100 px-1.5 py-0.5 rounded'
              )}
            >
              {t('lessons.lessonsCaptured')(lessons.length)}
            </span>
          </div>
          <div className="flex flex-col gap-2.5">
            {lessons.length === 0 && pendingLesson === null ? (
              <p className="text-sm text-slate-500 italic py-2 px-1 text-center">
                {t('lessons.lessonsEmpty')}
              </p>
            ) : (
              lessons.map((lesson, lessonIndex) => (
                <LessonRow
                  key={lessonIndex}
                  lesson={lesson}
                  onChange={(next) => onEditLesson(lessonIndex, next)}
                  onDelete={() => onDeleteLesson(lessonIndex)}
                />
              ))
            )}
            {pendingLesson !== null && (
              <PendingLessonRow
                onCommit={handlePendingCommit}
                onCancel={() => setPendingLesson(null)}
              />
            )}
            <button
              type="button"
              onClick={() => setPendingLesson('')}
              disabled={pendingLesson !== null}
              className="w-full mt-1.5 py-2.5 px-3 text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-100 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center gap-2 transition-all"
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('lessons.addLesson')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
